/**
 * Sets BlocklyApp constants that depend on the page and level.
 * This encapsulates many functions used for StudioApp.requiredBlocks_.
 * In the future, some of these may be moved to common.js.
 */

var requiredBlockUtils = require('../required_block_utils');

// This tests for and creates a draw_a_square block on page 2.
function drawASquare(number) {
  return {
    test: 'draw_a_square',
    type: 'draw_a_square',
    values: {VALUE: requiredBlockUtils.makeMathNumber(number)},
  };
}

// This tests for and creates a draw_a_snowman block on page 2.
function drawASnowman(number) {
  return {
    test: 'draw_a_snowman',
    type: 'draw_a_snowman',
    values: {VALUE: requiredBlockUtils.makeMathNumber(number)},
  };
}

// This tests for and creates the limited "move forward" block used on the
// earlier levels of the tutorial.
var MOVE_FORWARD_INLINE = {test: 'moveForward', type: 'draw_move_by_constant'};

// allow move forward or backward, but show forward block if they've done neither
var MOVE_FORWARD_OR_BACKWARD_INLINE = {
  test: function (block) {
    return block.type === 'draw_move_by_constant';
  },
  type: 'draw_move_by_constant',
};

// This tests for and creates the limited "move forward" block used on the
// earlier levels of the tutorial with the given pixel number.
var moveForwardInline = function (pixels) {
  return {
    test: 'moveForward',
    type: 'draw_move_by_constant',
    titles: {VALUE: pixels},
  };
};

// This tests for and creates the limited "move forward" block used on the
// earlier levels of the tutorial.
var MOVE_BACKWARD_INLINE = {
  test: 'moveBackward',
  type: 'draw_move_by_constant',
  titles: {DIR: 'moveBackward'},
};

// This tests for a [right] draw_turn_by_constant_restricted block
// and creates the block with the specified/recommended number of degrees as
// its input.  The restricted turn is used on the earlier levels of the
// tutorial.
var turnRightRestricted = function (degrees) {
  return {
    test: 'turnRight(',
    type: 'draw_turn_by_constant_restricted',
    titles: {VALUE: degrees},
  };
};

// This tests for a [left] draw_turn_by_constant_restricted block
// and creates the block with the specified/recommended number of degrees as
// its input.  The restricted turn is used on the earlier levels of the
// tutorial.
var turnLeftRestricted = function (degrees) {
  return {
    test: 'turnLeft(',
    type: 'draw_turn_by_constant_restricted',
    titles: {VALUE: degrees},
  };
};

// This tests for and creates a [right] draw_turn_by_constant block
// with the specified number of degrees as its input.
var turnRightByConstant = function (degrees) {
  return {
    test: function (block) {
      return (
        block.type === 'draw_turn_by_constant' &&
        (degrees === '???' ||
          Blockly.JavaScript.valueToCode(
            block,
            'VALUE',
            Blockly.JavaScript.ORDER_NONE
          ) === degrees)
      );
    },
    type: 'draw_turn_by_constant',
    titles: {VALUE: degrees},
  };
};

// This tests for and creates a [right] draw_turn block with the specified
// number of degrees as its input.  For the earliest levels, the method
// turnRightRestricted should be used instead.
var turnRight = function (degrees) {
  return {
    test: function (block) {
      return (
        block.type === 'draw_turn' && block.getFieldValue('DIR') === 'turnRight'
      );
    },
    type: 'draw_turn',
    titles: {DIR: 'turnRight'},
    values: {VALUE: requiredBlockUtils.makeMathNumber(degrees)},
  };
};

// This tests for and creates a left draw_turn block with the specified
// number of degrees as its input.  This method is not appropriate for the
// earliest levels of the tutorial, which do not provide draw_turn.
var turnLeft = function (degrees) {
  return {
    test: function (block) {
      return (
        block.type === 'draw_turn' && block.getFieldValue('DIR') === 'turnLeft'
      );
    },
    type: 'draw_turn',
    titles: {DIR: 'turnLeft'},
    values: {VALUE: requiredBlockUtils.makeMathNumber(degrees)},
  };
};

// This tests for any draw_move block and, if not present, creates
// one with the specified distance.
var move = function (distance) {
  return {
    test: function (block) {
      return block.type === 'draw_move';
    },
    type: 'draw_move',
    values: {VALUE: requiredBlockUtils.makeMathNumber(distance)},
  };
};

// This tests for and creates a draw_turn_by_constant_restricted block.
var drawTurnRestricted = function (degrees) {
  return {
    test: function (block) {
      return block.type === 'draw_turn_by_constant_restricted';
    },
    type: 'draw_turn_by_constant_restricted',
    titles: {VALUE: degrees},
  };
};

// This tests for and creates a draw_turn block.
var drawTurn = function () {
  return {
    test: function (block) {
      return block.type === 'draw_turn';
    },
    type: 'draw_turn',
    values: {VALUE: requiredBlockUtils.makeMathNumber('???')},
  };
};

// This tests for and creates a "set colour" block with a colour picker
// as its input.
var SET_COLOUR_PICKER = {
  test: "penColour('#",
  type: 'draw_colour',
  values: {COLOUR: '<block type="colour_picker"></block>'},
};

// This tests for and creates a "set colour" block with a random colour
// generator as its input.
var SET_COLOUR_RANDOM = {
  test: 'penColour(colour_random',
  type: 'draw_colour',
  values: {COLOUR: '<block type="colour_random"></block>'},
};

/**
 * Creates a required block specification for defining a function with an
 * argument.  Unlike the other functions to create required blocks, this
 * is defined outside of Turtle.setBlocklyAppConstants because it is accessed
 * when checking for a procedure on levels 8-9 of Turtle 3.
 * @param {string} func_name The name of the function.
 * @param {string} arg_name The name of the single argument.
 * @return A required block specification that tests for a call of the
 *     specified function with the specified argument name.  If not present,
 *     this contains the information to create such a block for display.
 */
var defineWithArg = function (func_name, arg_name) {
  return {
    test: function (block) {
      return (
        block.type === 'procedures_defnoreturn' &&
        block.getFieldValue('NAME') === func_name &&
        block.parameterNames_ &&
        block.parameterNames_.length &&
        block.parameterNames_[0] === arg_name
      );
    },
    type: 'procedures_defnoreturn',
    titles: {NAME: func_name},
    extra: '<mutation><arg name="' + arg_name + '"></arg></mutation>',
  };
};

module.exports = {
  makeMathNumber: requiredBlockUtils.makeMathNumber,
  simpleBlock: requiredBlockUtils.simpleBlock,
  repeat: requiredBlockUtils.repeat,
  drawASquare: drawASquare,
  drawASnowman: drawASnowman,
  MOVE_FORWARD_INLINE: MOVE_FORWARD_INLINE,
  MOVE_FORWARD_OR_BACKWARD_INLINE: MOVE_FORWARD_OR_BACKWARD_INLINE,
  moveForwardInline: moveForwardInline,
  MOVE_BACKWARD_INLINE: MOVE_BACKWARD_INLINE,
  turnLeftRestricted: turnLeftRestricted,
  turnRightRestricted: turnRightRestricted,
  turnRightByConstant: turnRightByConstant,
  turnRight: turnRight,
  turnLeft: turnLeft,
  move: move,
  drawTurnRestricted: drawTurnRestricted,
  drawTurn: drawTurn,
  SET_COLOUR_PICKER: SET_COLOUR_PICKER,
  SET_COLOUR_RANDOM: SET_COLOUR_RANDOM,
  defineWithArg: defineWithArg,
};
